import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import NInput from '../../components/inputs/NInput';
import NButton from '../../components/inputs/NButton';
import { addApplicationValidation } from '../../validations/validation';
import useApplicationAPI from '../../hooks/network/useApplicationAPI';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import { ResponseMessage } from '../../constants/Constants';
import { RouteEnums } from '../../Routes/RoutesConstant';
import { useFormHook } from '../../hooks/custom-hooks/useFormHook';

const formInitialValues = {
  name: '',
  code: '',
  url: '',
  secretKey: '',
  audienceKey: '',
  description: '',
};

function AddApplication() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { getApplication, updateApplication, addApplication, isLoading } = useApplicationAPI();
  const { formRef, formHeight } = useFormHook();

  const [formValues, setFormValues] = useState(formInitialValues);

  useEffect(() => {
    if (applicationId) {
      fetchApplicationDetails();
    }
  }, [applicationId]);

  const fetchApplicationDetails = async () => {
    try {
      const response = await getApplication(applicationId);
      setFormValues(response);
    } catch {}
  };

  const handleFormSubmit = async (values, formikBag) => {
    try {
      if (applicationId) {
        const response = await updateApplication(applicationId, values);
        if (response?.success) {
          successToaster(response?.message || 'Application Updated');
          navigate(RouteEnums.APPLICATION);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      } else {
        const response = await addApplication(values);
        if (response?.success) {
          formikBag.resetForm();
          setFormValues(formInitialValues);
          successToaster(response?.message || 'Application created');
          navigate(RouteEnums.APPLICATION);
        } else errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
      }
    } catch {}
  };

  return (
    <>
      <div className='nmobile-form-blur' />
      <div
        style={{
          height:
            window.innerWidth < 1180
              ? `min(calc(100vh - 150px), calc(100vh - ${formHeight}px))`
              : '100%',
        }}
        className='nform-wrapper'
      >
        <div className='nform-container'>
          {/* Mobile Header Heading */}
          <div className='nmobile-title'>
            <h2 className='app-heading h2-style'>{applicationId ? 'Edit' : 'Add'} Application</h2>
            <div onClick={() => navigate(RouteEnums.APPLICATION)}>
              <CloseIcon />
            </div>
          </div>

          <div className='nform'>
            <div ref={formRef}>
              <Formik
                initialValues={formValues}
                validationSchema={addApplicationValidation}
                onSubmit={(values, formikBag) => {
                  handleFormSubmit(values, formikBag);
                }}
                enableReinitialize
              >
                {({ touched, values, errors, handleSubmit, handleChange, handleBlur }) => (
                  <FormikForm className='' autoComplete='off' onSubmit={handleSubmit}>
                    {/* Desktop buttons */}
                    <div className='ntitle-nAction-button'>
                      <h2 className='app-heading h2-style'>
                        {applicationId ? 'Edit' : 'Create'} Applications
                      </h2>
                      <div className='naction-btns'>
                        <div>
                          <NButton
                            variant='Noutline'
                            onClick={() => navigate(RouteEnums.APPLICATION)}
                          >
                            Cancel
                          </NButton>
                        </div>
                        <div>
                          <NButton
                            isLoading={isLoading}
                            onClick={handleSubmit}
                            variant='Ncontained'
                          >
                            {applicationId ? 'Update' : 'Save'} Application
                          </NButton>
                        </div>
                      </div>
                    </div>

                    {/* Common form */}
                    <div className='nform-fields'>
                      <div className='w-full'>
                        <NInput
                          required
                          name='name'
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.name && touched.name}
                          helperText={errors.name && touched.name ? errors.name : ''}
                          className='w-full'
                          label='Application Name'
                          placeholder='Enter Application Name'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          required
                          name='code'
                          value={values.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.code && touched.code}
                          helperText={errors.code && touched.code ? errors.code : ''}
                          className='w-full'
                          label='Application Code'
                          placeholder='Enter Application Code'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          required
                          name='url'
                          value={values.url}
                          onChange={(e) => handleChange(e)}
                          onBlur={handleBlur}
                          error={errors.url && touched.url}
                          helperText={errors.url && touched.url ? errors.url : ''}
                          className='w-full'
                          label='Auth API'
                          placeholder='Enter Auth API'
                          type='text'
                        />
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='secretKey'
                          value={values.secretKey}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.secretKey && touched.secretKey}
                          helperText={errors.secretKey && touched.secretKey ? errors.secretKey : ''}
                          className='w-full'
                          label='Secret Key'
                          placeholder='Enter Secret Key'
                          type='text'
                        />
                      </div>
                      <div className=''>
                        <div className='w-full break:mb-10'>
                          <NInput
                            name='audienceKey'
                            value={values.audienceKey}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.audienceKey && touched.audienceKey}
                            helperText={
                              errors.audienceKey && touched.audienceKey ? errors.audienceKey : ''
                            }
                            className='w-full'
                            label='Audience Key'
                            placeholder='Enter Audience Key'
                            type='text'
                          />
                        </div>
                      </div>
                      <div className='w-full'>
                        <NInput
                          name='description'
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.description && touched.description}
                          helperText={
                            errors.description && touched.description ? errors.description : ''
                          }
                          className='w-full'
                          label='Description'
                          placeholder='Enter description'
                          type='text'
                          multiline
                          rows={7}
                        />
                      </div>
                    </div>

                    {/* Mobile Buttons */}
                    <div className='nmobile-action-btn'>
                      <div>
                        <NButton isLoading={isLoading} onClick={handleSubmit} variant='Ncontained'>
                          {applicationId ? 'Update' : 'Save'} Application
                        </NButton>
                      </div>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddApplication;
